import { setCookie } from '@app/config/cookies_action';
import i18n from '@app/config/i18n';
import { analyticsService } from '@common/analytics';
import {
  logInAPI,
  registerAPI,
  authSocAPI,
  loginByPhoneAPI
} from '@common/api/api';
import Const from '../config/const';
import {
  toCamelCase, getDataFromStorage, saveDataToStorage, paramOrNul, removeDataFromStorage
} from '../../common/config/utils';
import { log, LOG_LEVELS, trackToBugsnagApiError } from '../../common/config/app_logger';
import { loadHomePage } from './home_page';
import { GDPRConsernPopupThemes } from '../config/themes';

const operationsStatuses = {
  no_consent_privacy_policy: 'noConsent'
};

const authCookieName = 'is_logged_in';

const getCookieRootDomain = () => {
  const rootDomain = window.location.hostname.split('.').slice(-2).join('.');
  return `.${ rootDomain }`;
};

export const shouldShowGDPRConsent = (shouldDisplay, socialParams) => (dispatch) => {
  dispatch({
    type: Const.auth.showGDPRConsernPopup,
    display: shouldDisplay,
    socialParams
  });
};

const authProgressActionCreator = () => ({
  type: Const.auth.loading
});

export const authErrorActionCreator = error => ({
  type: Const.auth.error,
  payload: {
    error
  }
});

const authSuccessActionCreator = (user) => ({
  type: Const.auth.success,
  payload: {
    user
  }
});

export const navigationPop = () => ({
  type: Const.auth.navigationPop
});

export const authClearPendingAction = () => ({
  type: Const.auth.clearPendingAction
});

export const authClearPendingCloseAction = () => ({
  type: Const.auth.clearPendingCloseAction
});

export const changeScreen = (screen) => ({
  type: Const.auth.screenChange,
  nextScreen:screen
});

export const updateCurrentScreen = (screen) => ({
  type: Const.auth.updateCurrentScreen,
  newScreen:screen
});

export const gdprSwitcherValueChanged = (value) => ({
  type: Const.auth.consentToGDPRChanged,
  value
});

export const displayRegister = ({ shouldDisplay, signUpFlow, screen }) => {
  const wasLogin = getDataFromStorage('WAS_LOGIN');
  const getScreen = () => {
    if (screen) return screen;
    return wasLogin ? Const.authScreens.signIn : Const.authScreens.signUp;
  };
  return ({
    type: Const.auth.displayRegister,
    shouldDisplay,
    signUpFlow,
    currentScreen: getScreen()
  });
};

export const clearError = () => ({
  type: Const.auth.clearError
});

export const authPendingAction = pendingAction => dispatch => {
  const pendingDecorator = () => {
    pendingAction();
    dispatch(authClearPendingAction());
    dispatch(authClearPendingCloseAction());
  };
  dispatch({
    type: Const.auth.pendingAction,
    payload: {
      pendingAction: pendingDecorator
    }
  });
};

export const authPendingCloseAction = pendingCloseAction => dispatch => {
  const pendingCloseDecorator = () => {
    pendingCloseAction();
    dispatch(authClearPendingCloseAction());
    dispatch(authClearPendingAction());
  };
  dispatch({
    type: Const.auth.pendingCloseAction,
    payload: {
      pendingCloseAction: pendingCloseDecorator
    }
  });
};

const authHandlerSuccessAction = (user, dispatch, resolve) => {
  dispatch(authSuccessActionCreator(user));
  saveDataToStorage('WAS_LOGIN', true);
  resolve(user);
};

const mapLoginTypeToSignUpMethod = loginType => {
  const typesList = {
    google: 'google plus',
    email: 'email',
    facebook: 'facebook',
    phone_number: 'phone number'
  };
  return typesList[loginType];
};

const trackLoginEvent = (signupFlow) => {
  const { user: { user: { loginType } } } = window.store.getState();
  const eventName = 'sign in';
  const properties = {
    'signup flow': signupFlow || '',
    'signup method': mapLoginTypeToSignUpMethod(loginType),
    'signin method': mapLoginTypeToSignUpMethod(loginType)
  };

  analyticsService.trackEvent(eventName, properties);
};

const trackRegisterEvent = (signupFlow, conversionEventId, consentToMarketingSms) => {
  const eventName = 'signup';
  const { user: { user: { loginType, countryName } } } = window.store.getState();
  const properties = {
    'signup flow': signupFlow,
    'signup method': mapLoginTypeToSignUpMethod(loginType),
    'registration country name': countryName,
    ...paramOrNul('urgency sentence string', getDataFromStorage('advisor_urgency_signal_text'))
  };

  if (typeof consentToMarketingSms === 'boolean') {
    properties['marketing sms consent'] = consentToMarketingSms;
  }

  analyticsService.trackEvent(eventName, properties, { conversionEventId });
};

const setupPromoCodeListener = (dispatch) => {
  const handlePromoCodeComplete = () => {
    dispatch(loadHomePage(true));
    window.removeEventListener('promoCodeProcessingComplete', handlePromoCodeComplete);
  };
  window.addEventListener('promoCodeProcessingComplete', handlePromoCodeComplete);
};

// Authorize user in analytics before tracking events to ensure events are attributed to the correct user
// This prevents race conditions where events might be tracked before useEffect in common_logic_component.js fires
// const authorizeUserForAnalytics = (user, dispatch) => {
//   const userId = String(user.user?.id || window.currentUserId);
//   const { user: { user: currentUser } } = window.store.getState();
//   const mixpanelAliasCreated = currentUser?.mixpanelAliasCreated || user.user?.mixpanelAliasCreated || false;

//   analyticsService.authorize(userId, {
//     mixpanelAliasCreated,
//     onAliasCreated: () => {
//       // Update mixpanelAliasCreated in Redux store if needed
//       const { user: { user: stateUser } } = window.store.getState();
//       if (stateUser && !stateUser.mixpanelAliasCreated) {
//         dispatch({
//           type: Const.user.updateUserData,
//           user: { mixpanelAliasCreated: true }
//         });
//       }
//     }
//   });
// };

const authHandler = (promise, dispatch, socialParams, options = {}) => new Promise(async (resolve, reject) => promise
  .then(user  => {
    log(LOG_LEVELS.ERROR, 'Auth', 'FB social log in success we have success callback');
    const {
      user: { id, justSignedUp }
    } = user;
    window.currentUserId = id;

    const authHandlerSuccessActionWithResUserIfNeed = (resUser) => {
      if (resUser) {
        user.user = { ...user.user,  ...resUser };
      }
      authHandlerSuccessAction(user, dispatch, resolve);
    };

    if (justSignedUp) {
      authHandlerSuccessActionWithResUserIfNeed();
      return;
    }
    removeDataFromStorage('advisor_urgency_signal');
    removeDataFromStorage('advisor_urgency_signal_text');
    authHandlerSuccessActionWithResUserIfNeed();
    setCookie({ cookieName: authCookieName, cookieValue: true, cookieOptions: { path: '/', domain: getCookieRootDomain(), expires: new Date(Date.now() + (30 * 24 * 60 * 60 * 1000)) } });
  })
  .catch(error => {
    log(LOG_LEVELS.ERROR, 'Auth failed', 'log in error using social params', error?.message || error, socialParams);
    if (!('json' in error)) {
      let errorToString = 'Initial error';
      try {
        errorToString = JSON.stringify(error);
      } catch {
        errorToString = error?.message || error;
      }
      trackToBugsnagApiError('Auth FB failed', errorToString);
      dispatch(authErrorActionCreator([i18n.t('internal_error')]));
      reject(error);
      return;
    }

    // Check error status before processing
    const { status } = error;

    error
      .json()
      .then(jsonError => {
        const camelCaseError = toCamelCase(jsonError);
        const { errors, operationStatus, fullMessages } = camelCaseError;
        if (operationsStatuses[operationStatus]) {
          dispatch(shouldShowGDPRConsent({ [GDPRConsernPopupThemes.social]: true }, socialParams));
          return;
        }

        // For no_backup_email operationStatus - don't call authErrorActionCreator if this option is set
        // This allows component to handle navigation to email screen
        if (operationStatus === 'no_backup_email' && options.skipErrorDispatch) {
          // Clear loading state by dispatching empty error (sets loading: false in reducer)
          dispatch(authErrorActionCreator({}));
          // Preserve status and operationStatus in rejected error
          const errorToReject = new Error('EMAIL_REQUIRED');
          Object.assign(errorToReject, {
            ...(errors || camelCaseError),
            status: status || 422,
            operationStatus
          });
          reject(errorToReject);
          return;
        }

        // For 422 errors without no_backup_email (e.g., "Invalid confirmation code") - skip dispatch but pass error through
        // This allows component to display error message on verification screen
        if (status === 422 && options.skipErrorDispatch) {
          // Clear loading state by dispatching empty error (sets loading: false in reducer)
          dispatch(authErrorActionCreator({}));
          // Preserve error details but don't mark as EMAIL_REQUIRED
          const errorToReject = new Error('VALIDATION_ERROR');
          Object.assign(errorToReject, {
            ...(errors || camelCaseError),
            status: 422,
            operationStatus,
            fullMessages: fullMessages || camelCaseError.full_messages,
            full_messages: camelCaseError.full_messages || fullMessages
          });
          reject(errorToReject);
          return;
        }

        // For 409 error (email already exists) - don't call authErrorActionCreator if this option is set
        if (status === 409 && options.skipErrorDispatch) {
          // Preserve status and errors in rejected error
          const errorToReject = new Error('EMAIL_ALREADY_EXISTS');
          Object.assign(errorToReject, {
            ...(errors || camelCaseError),
            status: 409,
            email: errors?.email,
            fullMessages
          });
          reject(errorToReject);
          return;
        }

        // If errors exists, merge it with fullMessages to preserve it
        // Otherwise use the full camelCaseError object
        const errorPayload = errors ? { ...errors, ...(fullMessages && { fullMessages }) } : camelCaseError;
        dispatch(authErrorActionCreator(errorPayload));
        reject(errors || camelCaseError);
      })
      .catch(e => {
        dispatch(authErrorActionCreator(e));
        reject(e);
      });
  }));

export const authSocial = (socRequest, signupFlow) => (dispatch, getState) => {
  log(LOG_LEVELS.ERROR, 'Auth', 'FB social log at our backend starting');
  const { consentedToGDPR, loading } = getState().auth;
  if (loading) return;
  dispatch(authProgressActionCreator());
  const params = { ...socRequest, consentedToGDPR };
  const socialParams = { socRequest, signupFlow };
  log(LOG_LEVELS.ERROR, 'Auth', 'FB social log at our backend called with params');
  authHandler(authSocAPI(params), dispatch, socialParams)
    .then(user => {
      log(LOG_LEVELS.ERROR, 'Auth', 'FB social log at our backend success callback');
      if (user) {
        // authorizeUserForAnalytics(user, dispatch);
        if (user.user.justSignedUp) {
          trackRegisterEvent(signupFlow, user.conversionEventId);
          window.dispatchEvent(new CustomEvent('userRegistered', {
            detail: { user, signupFlow }
          }));
        } else {
          trackLoginEvent(signupFlow);
          window.dispatchEvent(new CustomEvent('userLoggedIn', {
            detail: { user, signupFlow }
          }));
        }
      }
    }).catch(e => { log(LOG_LEVELS.ERROR, 'Auth', 'log in error', e); });
};

export const logIn = (email, password, rememberMe, signupFlow, recaptchaToken) => (dispatch, getState) => {
  const { loading } = getState().auth;
  if (loading) return;
  dispatch(authProgressActionCreator());
  authHandler(logInAPI(email, password, rememberMe, recaptchaToken), dispatch)
    .then(user => {
      if (user) {
        // authorizeUserForAnalytics(user, dispatch);
        trackLoginEvent(signupFlow);
        window.dispatchEvent(new CustomEvent('userLoggedIn', {
          detail: { user, signupFlow }
        }));
        setupPromoCodeListener(dispatch);
      }
    }).catch(e => { log(LOG_LEVELS.ERROR, 'Auth', 'log in error', e); });
};

export const logInByPhone = (phoneNumber, confirmationCode, consentedToGDPR, consentToMarketingSms, signupFlow) => (dispatch, getState) => {
  const { loading } = getState().auth;
  if (loading) return Promise.resolve();

  dispatch(authProgressActionCreator());

  return authHandler(
    loginByPhoneAPI({
      phoneNumber, confirmationCode, consentedToGDPR, consentToMarketingSms
    }),
    dispatch,
    null,
    { skipErrorDispatch: true } // Skip error dispatch for 422
  )
    .then(user => {
      if (user) {
        // authorizeUserForAnalytics(user, dispatch);
        dispatch(loadHomePage(true));
        trackLoginEvent(signupFlow);
        window.dispatchEvent(new CustomEvent('userLoggedIn', {
          detail: { user, signupFlow }
        }));
        setupPromoCodeListener(dispatch);
      }
      return user;
    })
    .catch(error => {
      // If it's no_backup_email operationStatus - clear loading and return special error for email screen navigation
      if (error && error.operationStatus === 'no_backup_email') {
        // Clear loading state since we're not dispatching error through authErrorActionCreator
        // Use authErrorActionCreator with empty error to clear loading but not show error
        dispatch(authErrorActionCreator({})); // This will set loading: false in reducer
        const customError = new Error('EMAIL_REQUIRED');
        customError.status = error.status || 422;
        customError.operationStatus = error.operationStatus;
        customError.errors = error.errors;
        throw customError;
      }
      // For 422 with "Invalid confirmation code" - let it be handled by component to show error on screen
      // For other errors (including 401) - error is already dispatched in authHandler
      // The error object from authHandler already contains all fields from API response
      // including fullMessages (converted from full_messages by toCamelCase)
      log(LOG_LEVELS.ERROR, 'Auth', 'log in by phone error', error);
      throw error;
    });
};

export const register = (email, password, rememberMe, signupFlow, recaptchaToken) => (dispatch, getState) => {
  const { loading } = getState().auth;
  if (loading) return;
  dispatch(authProgressActionCreator());
  authHandler(registerAPI(email, password, rememberMe, recaptchaToken)
    .then(user => {
      dispatch(loadHomePage(true));
      return user;
    }), dispatch)
    .then(user => {
      if (user) {
        // authorizeUserForAnalytics(user, dispatch);
        if (user.user.justSignedUp) {
          trackRegisterEvent(signupFlow, user.conversionEventId);
          window.dispatchEvent(new CustomEvent('userRegistered', {
            detail: { user, signupFlow }
          }));
        } else {
          trackLoginEvent(signupFlow);
          window.dispatchEvent(new CustomEvent('userLoggedIn', {
            detail: { user, signupFlow }
          }));
        }
        setupPromoCodeListener(dispatch);
      }
    }).catch(e => { log(LOG_LEVELS.ERROR, 'Auth', 'log in error', e); });
};

export const registerByPhone = (phoneNumber, confirmationCode, consentedToPrivacyPolicy, consentToMarketingSms, email, signupFlow) => (dispatch, getState) => {
  const { loading } = getState().auth;
  if (loading) return Promise.resolve();
  dispatch(authProgressActionCreator());
  return authHandler(loginByPhoneAPI({
    phoneNumber,
    confirmationCode,
    consentedToPrivacyPolicy,
    consentToMarketingSms,
    email
  })
    .then(user => {
      dispatch(loadHomePage(true));
      return user;
    }), dispatch, null, { skipErrorDispatch: true })
    .then(user => {
      if (user) {
        // authorizeUserForAnalytics(user, dispatch);
        if (user.user.justSignedUp) {
          trackRegisterEvent(signupFlow, user.conversionEventId, consentToMarketingSms);
          window.dispatchEvent(new CustomEvent('userRegistered', {
            detail: { user, signupFlow }
          }));
        } else {
          trackLoginEvent(signupFlow);
          window.dispatchEvent(new CustomEvent('userLoggedIn', {
            detail: { user, signupFlow }
          }));
        }
        setupPromoCodeListener(dispatch);
      }
    }) // Skip error dispatch for 409
    .catch(error => {
      // For 409 error (email already exists) - clear loading and return error without dispatch
      if (error && error.status === 409) {
        // Clear loading state since we're not dispatching error through authErrorActionCreator
        dispatch(authErrorActionCreator({})); // This will set loading: false in reducer
        // Return error with email field for component to display
        const customError = new Error('EMAIL_ALREADY_EXISTS');
        customError.status = 409;
        customError.errors = error.errors;
        customError.email = error.errors?.email;
        customError.fullMessages = error.fullMessages;
        throw customError;
      }
      // For other errors - error is already dispatched in authHandler
      log(LOG_LEVELS.ERROR, 'Auth', 'register by phone error', error);
      throw error;
    });
};
